import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import React, { useState } from 'react';
import { NodeViewWrapper } from '@tiptap/react';
import { FiPlus, FiTrash2, FiThumbsUp, FiThumbsDown, FiCheck, FiX } from 'react-icons/fi';

// Pros/Cons Node View Component
const ProsConsNodeView = ({ node, updateAttributes, selected }) => {
  const [isEditing, setIsEditing] = useState(true);
  const { pros, cons } = node.attrs;

  const handleAddPro = () => {
    updateAttributes({ pros: [...pros, ''] });
  };

  const handleAddCon = () => {
    updateAttributes({ cons: [...cons, ''] });
  };

  const handleUpdatePro = (index, value) => {
    const newPros = [...pros];
    newPros[index] = value;
    updateAttributes({ pros: newPros });
  };

  const handleUpdateCon = (index, value) => {
    const newCons = [...cons];
    newCons[index] = value;
    updateAttributes({ cons: newCons });
  };

  const handleDeletePro = (index) => {
    const newPros = pros.filter((_, i) => i !== index);
    updateAttributes({ pros: newPros });
  };

  const handleDeleteCon = (index) => {
    const newCons = cons.filter((_, i) => i !== index);
    updateAttributes({ cons: newCons });
  };

  return (
    <NodeViewWrapper className={`proscons-block ${selected ? 'selected' : ''}`}>
      <div className="proscons-header">
        <FiThumbsUp size={16} />
        <span>Pros & Cons</span>
        <button
          type="button"
          onClick={() => setIsEditing(!isEditing)}
          className="proscons-edit-toggle"
        >
          {isEditing ? 'Preview' : 'Edit'}
        </button>
      </div>
      <div className="proscons-grid">
        {/* Pros Section */}
        <div className="pros-section">
          <div className="proscons-section-header">
            <FiThumbsUp size={16} />
            <span>Pros</span>
          </div>
          <ul className="proscons-list">
            {pros.map((pro, index) => (
              <li key={index}>
                <FiCheck size={14} className="proscons-icon pro-icon" />
                {isEditing ? (
                  <div className="proscons-item-edit">
                    <input
                      type="text"
                      value={pro}
                      onChange={(e) => handleUpdatePro(index, e.target.value)}
                      placeholder="Enter advantage..."
                    />
                    <button
                      type="button"
                      onClick={() => handleDeletePro(index)}
                      className="proscons-delete"
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                ) : (
                  <span>{pro || 'Advantage'}</span>
                )}
              </li>
            ))}
          </ul>
          {isEditing && (
            <button type="button" onClick={handleAddPro} className="proscons-add">
              <FiPlus size={14} />
              Add Pro
            </button>
          )}
        </div>

        {/* Cons Section */}
        <div className="cons-section">
          <div className="proscons-section-header">
            <FiThumbsDown size={16} />
            <span>Cons</span>
          </div>
          <ul className="proscons-list">
            {cons.map((con, index) => (
              <li key={index}>
                <FiX size={14} className="proscons-icon con-icon" />
                {isEditing ? (
                  <div className="proscons-item-edit">
                    <input
                      type="text"
                      value={con}
                      onChange={(e) => handleUpdateCon(index, e.target.value)}
                      placeholder="Enter disadvantage..."
                    />
                    <button
                      type="button"
                      onClick={() => handleDeleteCon(index)}
                      className="proscons-delete"
                    >
                      <FiTrash2 size={12} />
                    </button>
                  </div>
                ) : (
                  <span>{con || 'Disadvantage'}</span>
                )}
              </li>
            ))}
          </ul>
          {isEditing && (
            <button type="button" onClick={handleAddCon} className="proscons-add">
              <FiPlus size={14} />
              Add Con
            </button>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
};

// ProsCons Extension
export const ProsCons = Node.create({
  name: 'proscons',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      pros: {
        default: ['Easy to use', 'Great features', 'Affordable'],
      },
      cons: {
        default: ['Limited options', 'Learning curve'],
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="proscons"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'proscons' }), 0];
  },

  addNodeView() {
    return ReactNodeViewRenderer(ProsConsNodeView);
  },

  addCommands() {
    return {
      insertProsCons:
        (attrs = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              pros: attrs.pros || ['Easy to use', 'Great features', 'Affordable'],
              cons: attrs.cons || ['Limited options', 'Learning curve'],
            },
          });
        },
    };
  },
});

export default ProsCons;
